/**
 * Recruiter Select Component
 *
 * Dropdown for selecting/assigning a recruiter to a candidate.
 * Auto-assigns to creator on new records, allows manual reassignment.
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/useAuth';
import { supabase } from '@/utils/supabase';
import { LwField } from '@/components/lw';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('RecruiterSelect', 'recruitment');

interface RecruiterOption {
  id: string;
  name: string;
  email: string;
}

interface RecruiterSelectProps {
  value?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  autoAssigned?: boolean;
}

export const RecruiterSelect: React.FC<RecruiterSelectProps> = ({
  value,
  onChange,
  disabled = false,
  autoAssigned = false
}) => {
  const { user } = useAuth();
  const [recruiters, setRecruiters] = useState<RecruiterOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecruiters = async () => {
      if (!user?.organization_id) {
        log.warn('No organization ID available');
        return;
      }

      try {
        log.debug('Fetching active recruiters', { organizationId: user.organization_id });

        const { data, error } = await supabase
          .from('users')
          .select('id, first_name, last_name, full_name, email')
          .eq('organization_id', user.organization_id)
          .eq('employment_status', 'active')
          .order('full_name', { ascending: true });

        if (error) {
          log.error('Failed to fetch recruiters', { error: error.message }, error);
          throw error;
        }

        const options: RecruiterOption[] = (data || []).map(u => ({
          id: u.id,
          name: u.full_name || `${u.first_name} ${u.last_name}`.trim() || 'Unknown',
          email: u.email || ''
        }));

        log.info('Loaded recruiters', { count: options.length });
        setRecruiters(options);
      } catch (error) {
        log.error('Error loading recruiters', {}, error as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecruiters();
  }, [user?.organization_id]);

  return (
    <LwField
      htmlFor="recruiter-select"
      label={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          Assigned recruiter
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info size={14} style={{ color: 'var(--lw-muted)', cursor: 'help' }} />
              </TooltipTrigger>
              <TooltipContent className="lw-ds">
                <p style={{ maxWidth: '20rem', margin: 0 }}>
                  The person responsible for managing this recruitment. Can be reassigned to transfer ownership.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </span>
      }
      hint={autoAssigned ? 'Auto-assigned when created' : undefined}
    >
      <Select value={value} onValueChange={onChange} disabled={disabled || loading}>
        <SelectTrigger id="recruiter-select" aria-label="Select assigned recruiter">
          <SelectValue placeholder={loading ? 'Loading recruiters...' : 'Select a recruiter...'} />
        </SelectTrigger>
        <SelectContent className="lw-ds">
          {recruiters.length === 0 && !loading && (
            <div style={{ padding: 8, fontSize: 13, color: 'var(--lw-muted)' }}>No active recruiters found</div>
          )}
          {recruiters.map(recruiter => (
            <SelectItem key={recruiter.id} value={recruiter.id}>
              <span style={{ fontWeight: 500 }}>
                {recruiter.name}
                {recruiter.email && (
                  <span style={{ fontSize: 11, color: 'var(--lw-muted)', fontWeight: 400 }}> ({recruiter.email})</span>
                )}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </LwField>
  );
};
